import { Pagination as DesignSystemPagination } from "@/design-system";

/**
 * Compatibility adapter for the app's established pagination API.
 *
 * The design-system source names these props `total` and `onChange`; retaining
 * `totalPages` and `onPageChange` here upgrades every existing caller without
 * coupling feature code to that implementation detail.
 *
 * **Deliberate deviation from BEHAVIOR.md.** The handoff says the pager "dims to
 * 60% and ignores input" while a page loads, and the DS component implements that
 * by coupling the dim to `pointerEvents: none`. That works for the reference,
 * whose 450ms loading window is a `setTimeout`. Against NetSuite a page can take
 * seconds, and a pager that swallows clicks for an unknown duration reads as a
 * hang — the user cannot tell "loading" from "broken", and cannot change their
 * mind. So `loading` here does **not** disable anything: it only marks the region
 * `aria-busy` for assistive tech, and the skeleton grid carries the visual signal.
 *
 * Clicking through mid-fetch is safe because `fetchProductsBy` is abortable — the
 * callers abort the in-flight request before issuing the next one, and the slice
 * gates `isLoading` on the newest requestId. Don't reintroduce the disabled state
 * without also solving the "how long will this take" problem.
 *
 * Changing page **scrolls to the top**, which BEHAVIOR.md does specify: "Page
 * change: scroll to top + 450ms skeleton state". It lives here rather than in each
 * caller so every pager behaves the same; all four callers are full-page lists.
 * Pass `scrollToTop={false}` for a pager inside a modal or its own scroll
 * container, where moving the window would be wrong.
 *
 * The jump is instant, matching the reference's `window.scrollTo(0, 0)`
 * (`Dens N Dente Tailwind.dc.html:3121`). Smooth scrolling would animate through
 * content that the skeleton is about to replace anyway, and from deep in a long
 * list it feels like lag. Instant is also the `prefers-reduced-motion`-safe choice.
 */
export default function Pagination({
  page,
  totalPages,
  onPageChange,
  loading = false,
  scrollToTop = true,
  className = "",
  style,
}) {
  if (totalPages <= 1) return null;

  const handleChange = (nextPage) => {
    onPageChange?.(nextPage);
    // The window is the scroll container — App.js's root is `min-h-screen flex
    // flex-col`, with no overflow container above the listings.
    if (scrollToTop) window.scrollTo(0, 0);
  };

  return (
    <div className="flex justify-center my-4" aria-busy={loading || undefined}>
      <DesignSystemPagination
        page={page}
        total={totalPages}
        onChange={handleChange}
        className={className}
        // The DS pager also self-disables for up to 1.5s after its own click
        // (its internal `busy`), which loses rapid page clicks. Its `style` is
        // spread after its own rules, so this is the override — and it's the
        // supported way to bend a vendored component without forking it.
        style={{ pointerEvents: "auto", ...style }}
      />
    </div>
  );
}
